import { svgDivisor } from "@-/dev.improve.haniverse.lib.svg-geometry";

/**
 * Camera math for the map's gestures. Every function is a worklet, because the gestures run on
 * the UI thread, and pure, so the jump-free behavior is unit tested.
 *
 * `scale` is screen pixels per map unit; the origin is the map point at the viewport's top-left.
 */
export interface ChinaMapCamera {
  originX: number;
  originY: number;
  scale: number;
}

/** The pinch fields the camera reads, as Gesture Handler reports them. */
export interface ChinaMapPinchEvent {
  focalX: number;
  focalY: number;
  numberOfPointers: number;
  scale: number;
}

/** What a pinch holds still: the map point under the fingers and the zoom it started from. */
export interface ChinaMapPinchAnchor {
  /** The map point under the fingers' centroid. */
  mapX: number;
  mapY: number;
  /** Fingers down when the anchor was taken. */
  pointers: number;
  /** Camera scale per unit of the gesture's cumulative `scale`. */
  scalePerGestureScale: number;
}

/** Screen pixels kept clear around a framed region, inside the area between toolbar and sheet. */
export const CHINA_MAP_FIT_PADDING = 16;

export function clamp(value: number, min: number, max: number): number {
  "worklet";
  return Math.min(Math.max(value, min), max);
}

/**
 * The origins that keep some of the map under `visibleCenter` (screen pixels from the viewport's
 * leading edge), so it can never be panned out of sight. Pass the center of the area between the
 * toolbar and the card, which framing also centers on: a framed region is
 * then always in bounds.
 */
export function getChinaMapOriginBounds(
  scale: number,
  visibleCenter: number,
  mapExtent: number,
): [number, number] {
  "worklet";
  const offset = visibleCenter / svgDivisor(scale);
  return [-offset, mapExtent - offset];
}

/**
 * Widens `bounds` to include `origin`. A pinch can leave the camera outside the pan bounds, and
 * clamping the next drag (or `withDecay`'s `clamp`) there would snap the map across the screen in
 * one frame. With the current origin included, a drag can still move back toward the map but
 * never further out.
 */
export function includeChinaMapOrigin(
  [min, max]: readonly [number, number],
  origin: number,
): [number, number] {
  "worklet";
  return [Math.min(min, origin), Math.max(max, origin)];
}

/**
 * Anchors a pinch at the current camera.
 *
 * The pinch re-anchors whenever a finger lifts or lands, which is what keeps the map still. On
 * both iOS and Android a pinch stays active until the last finger lifts, and its focal point is
 * the centroid of the fingers still down: lifting one finger moves it halfway across the spread
 * to the other. The gesture's `scale` also restarts from the new span when a finger lands again.
 */
export function anchorChinaMapPinch(
  camera: ChinaMapCamera,
  event: ChinaMapPinchEvent,
): ChinaMapPinchAnchor {
  "worklet";
  const scale = svgDivisor(camera.scale);
  return {
    mapX: camera.originX + event.focalX / scale,
    mapY: camera.originY + event.focalY / scale,
    pointers: event.numberOfPointers,
    scalePerGestureScale: scale / svgDivisor(event.scale),
  };
}

/**
 * How far through a flight from a camera at `fromScale` to one at `toScale` the scale and the
 * origin are, as shares of the way from their start to their end, once the flight is `progress`
 * (0–1) of the way there.
 *
 * Every map point slides in a straight line from where the first camera draws it on screen to where
 * the second does. That is a zoom about the one screen point both cameras agree on (or a plain pan
 * when their scales match), so whatever both show stays on screen throughout: a tapped province is
 * zoomed into from where it is. Easing the scale and the origin separately does not do this. Halfway
 * through a 10× zoom into a province at the edge of the screen, the scale is then already 5.5×
 * while the origin is only halfway over, which shows a stretch of map holding neither the start
 * nor the target. The zoom also advances evenly in perceived (logarithmic) terms.
 */
export function getChinaMapFlightShares(
  fromScale: number,
  toScale: number,
  progress: number,
): { origin: number; scale: number } {
  "worklet";
  const ratio = toScale / svgDivisor(fromScale);
  // The screen position of every map point is this share of the way to its end.
  const share = Math.abs(ratio - 1) < 1e-9 ? progress : (ratio ** progress - 1) / (ratio - 1);
  // The origin that keeps every point on that line is the scale-weighted mix of the two origins.
  const scale = fromScale + (toScale - fromScale) * share;
  return { origin: (share * toScale) / svgDivisor(scale), scale: share };
}

/** The camera `progress` (0–1) of the way through a flight (see `getChinaMapFlightShares`). */
export function interpolateChinaMapCamera(
  from: ChinaMapCamera,
  to: ChinaMapCamera,
  progress: number,
): ChinaMapCamera {
  "worklet";
  const shares = getChinaMapFlightShares(from.scale, to.scale, progress);
  return {
    originX: from.originX + (to.originX - from.originX) * shares.origin,
    originY: from.originY + (to.originY - from.originY) * shares.origin,
    scale: from.scale + (to.scale - from.scale) * shares.scale,
  };
}

/** The camera for a pinch update: the anchored map point stays under the fingers' centroid. */
export function stepChinaMapPinch(
  camera: ChinaMapCamera,
  anchor: ChinaMapPinchAnchor,
  event: ChinaMapPinchEvent,
  [minScale, maxScale]: readonly [number, number],
): { anchor: ChinaMapPinchAnchor; camera: ChinaMapCamera } {
  "worklet";
  if (event.numberOfPointers !== anchor.pointers) {
    return { anchor: anchorChinaMapPinch(camera, event), camera };
  }
  const unclampedScale = anchor.scalePerGestureScale * event.scale;
  const scale = clamp(unclampedScale, minScale, maxScale);
  return {
    // Past a zoom limit, re-base so reversing the pinch zooms back at once instead of first
    // unwinding the overshoot.
    anchor:
      scale === unclampedScale
        ? anchor
        : { ...anchor, scalePerGestureScale: scale / svgDivisor(event.scale) },
    camera: {
      originX: anchor.mapX - event.focalX / scale,
      originY: anchor.mapY - event.focalY / scale,
      scale,
    },
  };
}
